import { useEffect, useEffectEvent, type RefObject } from 'react';
import { focusElement, getTabbable } from './focus';
import { isTopLayer, lockBodyScroll, pushLayer } from './layers';

export interface DialogBehaviorOptions {
  open: boolean;
  containerRef: RefObject<HTMLElement | null>;
  /**
   * Element to focus when opened. Defaults to `[data-autofocus]`, then the first tabbable element
   * without `data-skip-initial-focus`, then the container.
   */
  initialFocusRef?: RefObject<HTMLElement | null> | undefined;
  onEscape?: (() => void) | undefined;
  /** Traps Tab focus inside the container (modal behaviour). Default true. */
  trapFocus?: boolean;
  lockScroll?: boolean;
  /** Move focus into the container on open. Default true (false for listbox popups). */
  autoFocus?: boolean;
  /** Restores focus to the previously focused element on close. Default true. */
  restoreFocus?: boolean;
  /** Called with a pointer-down outside the container (non-modal popovers). */
  onOutsidePointerDown?: ((event: PointerEvent) => void) | undefined;
  /** Elements that count as "inside" for outside-click detection (e.g. the trigger). */
  ignoreRefs?: RefObject<HTMLElement | null>[] | undefined;
}

/**
 * Shared modal/popover mechanics: registers a layer, moves focus in, traps Tab, handles Escape,
 * optionally locks body scroll, and restores focus to the trigger on close.
 */
export function useDialogBehavior({
  open,
  containerRef,
  initialFocusRef,
  onEscape,
  trapFocus = true,
  lockScroll = false,
  autoFocus = true,
  restoreFocus = true,
  onOutsidePointerDown,
  ignoreRefs,
}: DialogBehaviorOptions): void {
  const handleEscape = useEffectEvent(() => {
    onEscape?.();
  });
  const hasEscapeHandler = useEffectEvent(() => Boolean(onEscape));
  const handleOutside = useEffectEvent((event: PointerEvent) => {
    if (!onOutsidePointerDown) return;
    const target = event.target as Node | null;
    if (!target) return;
    const container = containerRef.current;
    if (container?.contains(target)) return;
    if (ignoreRefs?.some((ref) => ref.current?.contains(target))) return;
    onOutsidePointerDown(event);
  });
  const getInitialFocus = useEffectEvent(() => initialFocusRef?.current ?? null);

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    const layer = Symbol('layer');
    const releaseLayer = pushLayer(layer);
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlockScroll = lockScroll ? lockBodyScroll() : undefined;
    // A click outside moves focus to wherever the user clicked; do not steal it back.
    let closedByOutsidePointer = false;

    const tabbable = autoFocus ? getTabbable(container) : [];
    const initial =
      getInitialFocus() ??
      container.querySelector<HTMLElement>('[data-autofocus]') ??
      tabbable.find((el) => !el.hasAttribute('data-skip-initial-focus')) ??
      tabbable[0] ??
      container;
    if (autoFocus) focusElement(initial);

    function onKeyDown(event: KeyboardEvent) {
      if (!isTopLayer(layer) || !container) return;
      if (event.key === 'Escape' && hasEscapeHandler()) {
        event.preventDefault();
        event.stopPropagation();
        handleEscape();
        return;
      }
      if (event.key !== 'Tab' || !trapFocus) return;
      const tabbable = getTabbable(container);
      if (tabbable.length === 0) {
        event.preventDefault();
        focusElement(container);
        return;
      }
      const first = tabbable[0];
      const last = tabbable[tabbable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        focusElement(last);
      } else if (!event.shiftKey && (active === last || !container.contains(active))) {
        event.preventDefault();
        focusElement(first);
      }
    }

    function onFocusIn(event: FocusEvent) {
      if (!trapFocus || !isTopLayer(layer) || !container) return;
      const target = event.target as Node | null;
      if (target && !container.contains(target)) {
        focusElement(getTabbable(container)[0] ?? container);
      }
    }

    function onPointerDown(event: PointerEvent) {
      if (!isTopLayer(layer)) return;
      const target = event.target as Node | null;
      if (target && !container?.contains(target)) closedByOutsidePointer = true;
      handleOutside(event);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('pointerdown', onPointerDown);
      releaseLayer();
      unlockScroll?.();
      const focusWentElsewhere =
        closedByOutsidePointer &&
        document.activeElement instanceof HTMLElement &&
        document.activeElement !== document.body;
      if (
        restoreFocus &&
        !focusWentElsewhere &&
        previouslyFocused &&
        previouslyFocused.isConnected
      ) {
        focusElement(previouslyFocused);
      }
    };
  }, [open, containerRef, trapFocus, lockScroll, autoFocus, restoreFocus]);
}
